import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Button, Input, Space, Tag, Tooltip } from "antd";
import {
    SearchOutlined,
    SyncOutlined,
    UserOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ColumnsType } from "antd/es/table";

import InvoicesService, { IInvoiceWithFilters } from "../../services/http/services/InvoicesService";
import { EInvoiceStatus } from "../../enums";
import useAuth from "../../hooks/useAuth";
import { useGetColumnSearchPropsData } from "../../hooks/FilterTable/date";
import { useIdColumn } from "../../hooks/useIdColumn";
import { useTableParams } from "../../hooks/useTableParams";
import { getSortOrder } from "../../hooks/SortOrder";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import StatusTag from "./components/StatusTag";
import {
    convertDataBrParaUS,
    convertMoedaBRDoJSNovo,
    formatDateStringToDateAndTime,
} from "../../utils/Conversores";
import { EmitError } from "../../utils/EmitError";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { findChunksText, HIGHLIGHT_STYLE } from "../../utils/Highlight";

export interface ITableData extends IInvoiceWithFilters {
    rowKey: number | undefined;
    appUserName: string;
    appUserId: number;
    createdAt: Date;
}

export type TypeSortInvoice = 'createdAt' | 'type' | 'status' | 'value';

export interface ICityInvoicesFilters {
    company: string | null;
    appUserName: string | null;
    type: string | null;
    createdAt: string | null;
    status: string[] | string | null;
}

export default function useCityInvoices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const location = useLocation();
    const stateCompany = location.state?.company;
    const { activeCity, user } = useAuth();
    const isAdmin = user?.role?.id === 1;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const [showModalInvoice, setShowModalInvoice] = useState<boolean>(false);
    const [invoiceSelected, setInvoiceSelected] = useState<ITableData | undefined>();

    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const nameInputRef = useRef<any>(null);
    const companyInputRef = useRef<any>(null);

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
        lastSorterRef,
    } = useTableParams<ICityInvoicesFilters, TypeSortInvoice>({
        storageKey: '@NotaMunicipio:allInvoices',
        defaultFilters: {
            company: null,
            appUserName: null,
            type: null,
            createdAt: null,
            status: null,
        },
        defaultSort: {
            field: 'createdAt',
            order: 'desc',
        },
        defaultPageSize: 50,
    });

    const [searchAppUserName, setSearchAppUserName] = useState(tableParams.filters.appUserName || '');
    const [searchCompany, setSearchCompany] = useState(tableParams.filters.company || '');

    useEffect(() => {
        if (stateCompany) {
            handleCustomChange({
                newPage: 1,
                newFilters: { company: stateCompany },
            });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [stateCompany, handleCustomChange, location.pathname, navigate]);

    useEffect(() => {
        setSearchAppUserName(tableParams.filters.appUserName || '');
    }, [tableParams.filters.appUserName]);

    useEffect(() => {
        setSearchCompany(tableParams.filters.company || '');
    }, [tableParams.filters.company]);

    const handleShowModalInvoice = useCallback(() => {
        setShowModalInvoice((prevState) => !prevState);
    }, []);

    const reprocessInvoice = useCallback((invoiceId: number, appUserId: number) => {
        async function sendInvoiceToReprocess() {
            try {
                await InvoicesService.reprocessInvoice({
                    invoiceId,
                    appUserId: Number(appUserId),
                    cityId: activeCity?.id!,
                });

                EmitSuccessAlert('Nota enviada para reprocessamento!');
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'viewAppUserInvoices_reprocess');
            }
        }

        sendInvoiceToReprocess();
    }, [activeCity]);

    const handleClickImportCityInvoice = useCallback(() => {
        navigate(`/notas/geral/importacao`);
    }, [navigate]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.createdAt
            ? {
                key: 'createdAt',
                label: 'Data de Importação',
                value: tableParams.filters.createdAt,
                onClear: () => handleFilterClear('createdAt'),
            }
            : null,
        tableParams.filters.type
            ? {
                key: 'type',
                label: 'Tipo',
                value: tableParams.filters.type,
                onClear: () => handleFilterClear('type'),
            }
            : null,
        tableParams.filters.appUserName
            ? {
                key: 'appUserName',
                label: 'Usuário',
                value: tableParams.filters.appUserName,
                onClear: () => {
                    setSearchAppUserName('');
                    handleFilterClear('appUserName');
                },
            }
            : null,
        tableParams.filters.company
            ? {
                key: 'company',
                label: 'Empresa',
                value: tableParams.filters.company,
                onClear: () => {
                    setSearchCompany('');
                    handleFilterClear('company');
                },
            }
            : null,
        tableParams.filters.status
            ? {
                key: 'status',
                label: 'Status',
                value: Array.isArray(tableParams.filters.status)
                    ? tableParams.filters.status.join(', ')
                    : tableParams.filters.status,
                onClear: () => handleFilterClear('status'),
            }
            : null,
    ];

    const idColumn = useIdColumn({
        show: isAdmin,
        ref: refFuncaoLimparFiltrosString,
        onClick: (id) => navigate(`/nota/detalhes/${id}`),
    });

    const columns: ColumnsType<ITableData> = [
        idColumn ? { ...idColumn, width: 90 } : null,
        {
            title: 'Data de Importação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            sortOrder: getSortOrder('createdAt', tableParams.sorter),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: 'createdAt',
                ref: refFuncaoLimparFiltrosString,
                onFilterClear: () => handleFilterClear('createdAt'),
                initialValue: tableParams.filters.createdAt,
            }),
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data, true) : '--'}
                </span>
            ),
            width: 200,
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('type', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'NFS', value: 'NFS' },
                { text: 'NFC', value: 'NFC' },
                { text: 'NFE', value: 'NFE' },
            ],
            filteredValue: tableParams.filters?.type ? [tableParams.filters.type] : null,
            filterMultiple: false,
            render: (_: any, { type }: ITableData) => {
                let color = 'blue';
                if (type === 'NFC') color = 'purple';
                if (type === 'NFE') color = 'cyan';

                return (
                    <Tag
                        color={color}
                        style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                    >
                        {type ?? '--'}
                    </Tag>
                );
            },
            width: 100,
        },
        {
            title: 'Usuário',
            dataIndex: 'appUserName',
            key: 'appUserName',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar por Usuário
                    </div>
                    <Input
                        ref={nameInputRef}
                        placeholder="Nome do usuário..."
                        value={searchAppUserName}
                        onChange={(e) => setSearchAppUserName(e.target.value)}
                        onPressEnter={() => {
                            confirm();
                            handleCustomChange({
                                newPage: 1,
                                newFilters: { appUserName: searchAppUserName.trim() || null },
                            });
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { appUserName: searchAppUserName.trim() || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchAppUserName('');
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { appUserName: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: tableParams.filters.appUserName ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                    {tableParams.filters.appUserName ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[tableParams.filters.appUserName]}
                            findChunks={findChunksText}
                            autoEscape
                            textToHighlight={record.appUserName || '--'}
                        />
                    ) : (
                        record.appUserName || '--'
                    )}
                </span>
            ),
            width: 220,
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('value', tableParams.sorter),
            showSorterTooltip: false,
            render: (_: any, { value }: ITableData) => (
                <Tooltip color="#389e0d" title="Valor total da nota fiscal">
                    <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', fontSize: 13, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value)}
                    </span>
                </Tooltip>
            ),
            width: 130,
        },
        {
            title: 'Empresa',
            dataIndex: 'company',
            key: 'company',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar por Empresa
                    </div>
                    <Input
                        ref={companyInputRef}
                        placeholder="Nome do estabelecimento..."
                        value={searchCompany}
                        onChange={(e) => setSearchCompany(e.target.value)}
                        onPressEnter={() => {
                            confirm();
                            handleCustomChange({
                                newPage: 1,
                                newFilters: { company: searchCompany.trim() || null },
                            });
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { company: searchCompany.trim() || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchCompany('');
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { company: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: tableParams.filters.company ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => companyInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontSize: 13 }}>
                    {tableParams.filters.company ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[tableParams.filters.company]}
                            findChunks={findChunksText}
                            autoEscape
                            textToHighlight={record.company || '--'}
                        />
                    ) : (
                        record.company || '--'
                    )}
                </span>
            ),
            width: 240,
            ellipsis: true,
        },
        {
            title: 'Bilhetes',
            dataIndex: 'tickets',
            key: 'tickets',
            align: 'center' as const,
            render: (_: any, { tickets }: ITableData) => {
                const count = tickets ? tickets.length : 0;
                if (count === 0) {
                    return (
                        <Tag color="default" style={{ color: "#8c8c8c", borderRadius: 10, margin: 0, fontWeight: 500 }} bordered={false}>
                            0 Bilhetes
                        </Tag>
                    );
                }

                return (
                    <Tooltip title="Bilhetes gerados a partir desta nota fiscal">
                        <Tag color="#1677ff" style={{ borderRadius: 10, margin: 0, fontWeight: 600 }}>
                            {count} {count === 1 ? 'Bilhete' : 'Bilhetes'}
                        </Tag>
                    </Tooltip>
                );
            },
            width: 120,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('status', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'Concluído', value: 'Concluído' },
                { text: 'Recusado', value: EInvoiceStatus.error },
                { text: 'Alerta', value: EInvoiceStatus.warning },
                { text: 'Em processamento', value: EInvoiceStatus.processing },
                { text: 'Reprocessando', value: EInvoiceStatus.reprocessing },
            ],
            filteredValue: tableParams.filters?.status
                ? (Array.isArray(tableParams.filters.status) ? tableParams.filters.status : [tableParams.filters.status])
                : null,
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
            width: 170,
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 130,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar detalhes"
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            setInvoiceSelected(record);
                            handleShowModalInvoice();
                        }}
                    />
                    {record.appUserId ? (
                        <TableActionButton
                            action="custom"
                            tooltip="Ver cadastro do usuário"
                            icon={<UserOutlined style={{ fontSize: 14, color: '#52c41a' }} />}
                            color="#52c41a"
                            bgColor={isDark ? 'rgba(82, 196, 26, 0.15)' : '#f6ffed'}
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                navigate(`/usuarios/aplicativo/visualizar/${record.appUserId}`);
                            }}
                        />
                    ) : null}
                    {record.status === EInvoiceStatus.processing ? (
                        <TableActionButton
                            action="custom"
                            tooltip="Reprocessar nota"
                            icon={<SyncOutlined style={{ fontSize: 13, color: '#fa8c16' }} />}
                            color="#fa8c16"
                            bgColor={isDark ? 'rgba(250, 140, 22, 0.15)' : '#fff7e6'}
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                reprocessInvoice(record.id, record.appUserId);
                            }}
                        />
                    ) : null}
                </div>
            ),
        },
    ].filter(Boolean) as ColumnsType<ITableData>;

    const setTableDataFromRequest = useCallback((data: Array<IInvoiceWithFilters>) => {
        const newData = data.map((invoice) => ({
            ...invoice,
            rowKey: invoice.id,
            appUserName: invoice.app_user_name,
            appUserId: invoice.app_user_id,
            createdAt: invoice.created_at,
        }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function fetchInvoices() {
            try {
                setIsLoading(true);
                const response = await InvoicesService.listAllByCity({
                    cityId: activeCity?.id!,
                    page: tableParams.pagination.current,
                    size: tableParams.pagination.pageSize,
                    sort: tableParams.sorter.field ? (tableParams.sorter.field as TypeSortInvoice) : 'createdAt',
                    order: tableParams.sorter.order === 'asc' ? 'asc' : 'desc',
                    status: Array.isArray(tableParams.filters.status)
                        ? tableParams.filters.status
                        : tableParams.filters.status
                        ? [tableParams.filters.status]
                        : undefined,
                    type: tableParams.filters.type || undefined,
                    user: tableParams.filters.appUserName || undefined,
                    company: tableParams.filters.company || undefined,
                    createdAt: (tableParams.filters.createdAt && convertDataBrParaUS(tableParams.filters.createdAt)) || undefined,
                    signal: controller.signal,
                });

                setTableDataFromRequest(response.invoices);
                setTotalInvoices(response.total);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'cityInvoices');
                    setIsLoading(false);
                }
            }
        }

        fetchInvoices();

        return () => controller.abort();
    }, [activeCity, tableParams, setTableDataFromRequest]);

    return {
        columns,
        isLoading,
        tableData,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        tableParams,
        totalInvoices,
        activeFilterItems,
        lastSorterRef,
        showModalInvoice,
        handleShowModalInvoice,
        invoiceSelected,
        setInvoiceSelected,
        handleClickImportCityInvoice,
        navigate,
    };
}
